import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Card } from "@shared/ui/card";
import { Info, ChevronDown, ChevronUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import PreviewMessage from "../../../preview/preview";
import { useNotificationChoices } from "@features/ux/hooks";
import {
  useGetTemplateData,
  useRecipientList,
  useSendBulkNotification,
  useTemplateList,
} from "@features/ux/components/quickMessage/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";
import { useSignatureData } from "@features/ux/components/templates/hooks";
import { useGetChoices } from "@features/ux/components/shared/hooks";
import { useSearchParams } from "react-router-dom";

const CreateBulkMessage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    channel: "",
    messageType: "0",
    emailFormat: "0",
    recipientType: searchParams.get("recipientType") || "",
    searchRecipient: "",
    subject: "",
    message: "",
    emailBody: "",
    richTextBody: "",
    htmlTextBody: "",
    template: "",
    footer: "",
    signature: "",
    scheduleForLater: false,
  });
  const { data: signatureData } = useSignatureData();
  const { data: channelData } = useGetChoices(
    "channel_choice",
    "quick_messaging",
  );
  const { data: recipientTypeData } = useGetChoices(
    "audience_type",
    "quick_messaging",
  );
  const { data: templateTypeData } = useGetChoices("template_type");
  const { data: templateData } = useTemplateList(formData.recipientType);
  const { data: templateDetailData } = useGetTemplateData(formData.template);
  const { data: recipientListData, isLoading: isLoadingRecipients } =
    useRecipientList(formData.recipientType);

  const channelsOptions =
    channelData?.result?.map((channel) => ({
      value: channel.key,
      label: channel.value,
    })) || [];

  const recipientTypeOptions =
    recipientTypeData?.result?.map((recipientType) => ({
      value: recipientType.key,
      label: recipientType.value,
    })) || [];

  const templateTypeOptions =
    templateTypeData?.result?.map((templateType) => ({
      value: templateType.key,
      label: templateType.value,
    })) || [];

  const recipientListOptions =
    recipientListData?.results?.map((recipientList) => ({
      value: recipientList.id,
      label: recipientList.name,
    })) || [];
  const templateOptions =
    templateData?.results
      ?.filter((template) => template.statusDisplay === "Active")
      .map((template) => ({
        value: template.id,
        label: template.templateName,
      })) || [];
  const signatureOptions =
    signatureData?.results?.map((signature) => ({
      value: signature.id.toString(),
      label: signature.name,
      isDefault: signature.isDefault,
    })) || [];
  const defaultSignatureId =
    signatureData?.results
      ?.find((signature) => signature.isDefault)
      ?.id?.toString() || "";

  const { mutate: sendBulkNotification, isPending } = useSendBulkNotification();

  const placeholderData = [
    {
      group: "CUSTOMER",
      items: [
        { label: "First Name", value: "first_name" },
        { label: "Last Name", value: "last_name" },
        { label: "Full Name", value: "full_name" },
        { label: "Account Number", value: "account_number" },
        { label: "Email", value: "email" },
        { label: "Phone Number", value: "phone_number" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Billing Address", value: "billing_address" },
        { label: "Service Address", value: "service_address" },
        { label: "Plan Name", value: "plan_name" },
      ],
    },

    {
      group: "METER",
      items: [
        { label: "Meter Number", value: "meter_number" },
        { label: "Meter Type", value: "meter_type" },
        { label: "Device Number", value: "device_number" },
        { label: "Installation Date", value: "installation_date" },
        { label: "Previous Reading", value: "previous_reading" },
        { label: "Previous Reading Date", value: "previous_reading_date" },
        { label: "Current Reading", value: "current_reading" },
        { label: "Current Reading Date", value: "current_reading_date" },
        { label: "Consumption Units", value: "consumption_units" },
      ],
    },

    {
      group: "BILLING",
      items: [
        { label: "Invoice Number", value: "invoice_number" },
        { label: "Billing Cycle", value: "billing_cycle" },
        {
          label: "Billing Period Start Date",
          value: "billing_period_start_date",
        },
        { label: "Billing Period End Date", value: "billing_period_end_date" },
        { label: "Bill Amount", value: "bill_amount" },
        { label: "Due Date", value: "due_date" },
        { label: "Late Fee Amount", value: "late_fee_amount" },
        { label: "Disconnection Date", value: "disconnection_date" },
      ],
    },

    {
      group: "PAYMENT",
      items: [
        { label: "Payment ID", value: "payment_id" },
        { label: "Payment Date", value: "payment_date" },
        { label: "Payment Channel", value: "payment_channel" },
        { label: "Payment Mode", value: "payment_mode" },
        { label: "Payment Amount", value: "payment_amount" },
        { label: "Remaining Balance", value: "remaining_balance" },
        { label: "Receipt Number", value: "receipt_number" },
      ],
    },

    {
      group: "SERVICE ORDER / WORK ORDER",
      items: [
        { label: "Service Order ID", value: "service_order_id" },
        { label: "Service Type", value: "service_type" },
        { label: "Requested Date", value: "requested_date" },
        { label: "Requested Time", value: "requested_time" },
        { label: "Technician Name", value: "technician_name" },
        { label: "Status", value: "status" },
      ],
    },

    {
      group: "SELF SERVICE SUPPORT",
      items: [
        { label: "Service Center Name", value: "service_center_name" },
        { label: "Service Center Email", value: "service_center_email" },
        { label: "Service Center Number", value: "service_center_number" },
        { label: "Service Center Location", value: "service_center_location" },
      ],
    },
  ];

  const showWhenCondition = (fieldName: string) => {
    return (formValues: any) => {
      const messageType = formValues.messageType;
      const channel = formValues.channel;
      const emailFormat = formValues.emailFormat;
      const scheduleForLater = formValues.scheduleForLater;

      switch (fieldName) {
        case "template":
          return messageType === "1";

        case "emailFormat":
          return channel === "0" && messageType === "0";

        case "subject":
          return channel === "0";

        case "info":
          return channel === "0" && messageType === "0";

        case "emailBody":
          return (
            (messageType === "1" && channel === "0") ||
            (channel === "0" && messageType === "0" && emailFormat === "0")
          );

        case "richTextBody":
          return channel === "0" && messageType === "0" && emailFormat === "1";

        case "htmlTextBody":
          return channel === "0" && messageType === "0" && emailFormat === "2";

        case "footer":
        case "signature":
          return channel === "0" && messageType === "0";

        case "message":
          return (
            (messageType === "1" && channel === "1") ||
            (channel === "1" && messageType === "0")
          );

        case "scheduleDate":
          return scheduleForLater === true;

        default:
          return false;
      }
    };
  };

  const formFields: FormField[] = [
    {
      name: "channel",
      type: "select",
      label: "Channel",
      options: channelsOptions,
      defaultValue: "",
      required: true,
      helperText: "Select the communication channel for sending your message",
      className: "col-span-2",
    },
    {
      name: "messageType",
      type: "radio",
      label: "Message Type",
      options: [
        { label: "Custom Message", value: "0" },
        { label: "Use Template", value: "1" },
      ],
      defaultValue: "0",
      required: false,
      helperText:
        "Choose whether to compose a custom message or use a pre-defined template",
      className: "col-span-2",
    },

    {
      name: "recipientType",
      type: "select",
      label: "Recipient Type",
      options: recipientTypeOptions,
      required: true,
      helperText: "Select the type of recipient you want to search for",
      className: "col-span-2",
    },
    {
      name: "recipientList",
      type: "select",
      label: "Recipient List",
      options: recipientListOptions,
      required: true,
      helperText:
        "Choose the recipient list to send this message to. All members of the list will receive the message.",
      className: "col-span-2",
    },
    {
      name: "template",
      type: "select",
      label: "Select Template",
      options: templateOptions,
      required: false,
      showWhen: showWhenCondition("template"),
      helperText:
        "Select a template that matches your selected channel. The subject and message will be auto-filled.",
      className: "col-span-2",
    },
    {
      name: "emailFormat",
      type: "radio",
      label: "Email Format",
      groupColorNeeded: false,
      showWhen: showWhenCondition("emailFormat"),
      group: "Message Content",
      options: [
        { label: "Plain Text", value: "0" },
        { label: "Rich Text", value: "1" },
        { label: "HTML", value: "2" },
      ],
      defaultValue: "0",
      required: false,
      helperText: "Choose between plain text, rich text editor, or raw HTML",
      className: "col-span-2 mt-2",
    },
    {
      name: "subject",
      type: "text-with-placeholder",
      label: "",
      placeholder: "Enter email subject (use {placeholders})",
      placeholders: placeholderData,

      showWhen: showWhenCondition("subject"),
      required: false,
      disabled: formData.messageType === "1",
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Write a clear subject line. Use placeholders like {customer_name} for personalization",
      className: "col-span-2",
    },
    {
      name: "info",
      type: "custom",
      label: "",
      required: false,
      className: "col-span-2 mt-2",
      groupColorNeeded: false,
      group: "Message Content",
      showWhen: showWhenCondition("info"),
      customRender: () => {
        const [isOpen, setIsOpen] = useState(false);
        return (
          <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
            className="overflow-hidden border border-blue-300 rounded-lg bg-blue-50 px-6 py-4"
          >
            <CollapsibleTrigger asChild>
              <div className="flex justify-between items-center cursor-pointer -mx-6 px-6 py-2 rounded">
                <div className="flex items-center gap-3">
                  <Info
                    size={18}
                    className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0"
                  />
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    Using Placeholders for Dynamic Content
                  </span>
                </div>
                {isOpen ? (
                  <ChevronUp size={18} className="text-blue-600" />
                ) : (
                  <ChevronDown size={18} className="text-blue-600" />
                )}
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="pt-3">
                <div className="text-xs text-blue-800 dark:text-blue-200 space-y-3">
                  <p>
                    Placeholders allow you to personalize messages with dynamic
                    data. Use the format{" "}
                    <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                      {"{{placeholder_name}}"}
                    </code>{" "}
                    in your subject and message body.
                  </p>

                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Click "Insert Placeholder" to browse available
                        placeholders by category
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Placeholders will be highlighted in your message for
                        easy identification
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Common examples:{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{customer_name}}"}
                        </code>
                        ,{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{bill_amount}}"}
                        </code>
                        ,{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{due_date}}"}
                        </code>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      },
    },
    {
      name: "emailBody",
      type: "textarea-with-placeholder",
      label: "",
      placeholder: "Enter email body. Use placeholders for dynamic content",
      placeholders: placeholderData,
      required: false,
      rows: 8,
      disabled: formData.messageType === "1",
      showWhen: showWhenCondition("emailBody"),
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Write your message content. Click 'Insert Placeholder' to add dynamic fields",
      className: "col-span-2",
    },
    {
      name: "richTextBody",
      type: "rich-text",
      label: "Email Body (Rich Text)",
      placeholder: "Compose your email...",
      placeholderNeeded: true,
      placeholders: placeholderData,
      showWhen: showWhenCondition("richTextBody"),
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Use the toolbar to format your email. Placeholders can be inserted using the buttons below",
      required: false,
      className: "col-span-2",
    },
    {
      name: "htmlTextBody",
      type: "html-text",
      label: "Email Body (HTML Text)",
      placeholder: "Compose your email...",
      placeholderNeeded: true,
      placeholders: placeholderData,
      showWhen: showWhenCondition("htmlTextBody"),
      groupColorNeeded: false,
      group: "Message Content",
      helperText: "Write raw HTML code. Use placeholders for dynamic content",
      required: false,
      className: "col-span-2",
    },
    {
      name: "message",
      type: "textarea-with-placeholder",
      label: "",
      placeholder: "Enter your message (use {placeholders})",
      placeholders: placeholderData,
      disabled: formData.messageType === "1",
      showWhen: showWhenCondition("message"),
      required: false,
      rows: 8,
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Compose your message. Use placeholders like {customer_name} for personalization",
      className: "col-span-2",
    },
    {
      name: "footer",
      type: "textarea",
      label: "Footer",
      groupColorNeeded: false,
      group: "Message Content",
      showWhen: showWhenCondition("footer"),
      required: false,
      helperText: "This content will appear at the bottom of your email",
      className: "col-span-2",
    },
    {
      name: "signature",
      type: "select",
      label: "Signature",
      options: signatureOptions,
      defaultValue: defaultSignatureId,
      groupColorNeeded: false,
      group: "Message Content",
      showWhen: showWhenCondition("signature"),
      required: false,
      helperText: "Select a signature to append to your email",
      className: "col-span-2",
    },
    {
      name: "scheduleForLater",
      type: "checkbox",
      label: "Schedule for Later",
      className: "col-span-2 block text-sm font-medium text-gray-700",
      required: false,
    },
    {
      name: "scheduleDate",
      type: "datepicker",
      label: "Schedule Date",
      showWhen: showWhenCondition("scheduleDate"),
      className: "col-span-2",
      required: false,
      helperText: "Schedule this message for later delivery",
      allowPast: false,
    },
    {
      name: "scheduleTime",
      type: "time",
      label: "Schedule Time",
      showWhen: showWhenCondition("scheduleDate"),
      className: "col-span-2",
      required: false,
      helperText: "Schedule this message for later delivery",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    if (templateDetailData && formData.messageType === "1") {
      const templateContent = templateDetailData.content;

      // Auto-populate subject and message from template
      setFormData((prev) => ({
        ...prev,
        subject: templateContent.subject || "",
        message: templateContent.body || "",
        emailBody: templateContent.body || "",
        footer: templateContent.footer || "",
      }));

      // Update form values
      form.setValue("subject", templateContent.subject || "");
      form.setValue("footer", templateContent.footer || "");

      if (formData.channel === "1") {
        // SMS channel
        form.setValue("message", templateContent.body || "");
      } else {
        // Email channel
        form.setValue("emailBody", templateContent.body || "");
      }
    }
  }, [templateDetailData, formData.messageType, formData.channel, form]);
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    const recipientListParam = searchParams.get("recipientList");
    const recipientTypeParam = searchParams.get("recipientType");
    if (recipientListParam && recipientTypeParam) {
      setFormData((prev) => ({
        ...prev,
        recipientList: recipientListParam,
      }));
      setFormData((prev) => ({
        ...prev,
        recipientType: recipientTypeParam,
      }));
      form.setValue("recipientList", recipientListParam);
      form.setValue("recipientType", recipientTypeParam);
    }
  }, [recipientListData, searchParams, form]);
  const handleSubmit = async () => {
    const values = form.getValues();
    console.log("Submitting bulk message:", values);

    // Log based on selected format
    if (values.emailFormat === "1") {
      console.log("Rich text body:", values.richTextBody);
    } else if (values.emailFormat === "2") {
      console.log("HTML body:", values.htmlTextBody);
    }

    // Determine the body content based on channel and format
    let bodyContent = "";
    if (values.channel === "EMAIL") {
      if (values.emailFormat === "0") {
        bodyContent = values.emailBody;
      } else if (values.emailFormat === "1") {
        bodyContent = values.richTextBody;
      } else if (values.emailFormat === "2") {
        bodyContent = values.htmlTextBody;
      }
    } else if (values.channel === "SMS") {
      bodyContent = values.message;
    }

    const payload = {
      remote_utility_id: getRemoteUtilityId(),
      channel_type: values.channel,
      list_id: values.recipientList,
      template_id: values.template ? values.template : null,
      schedule_date: values.scheduleForLater ? values.scheduleDate : null,
      schedule_time: values.scheduleForLater ? values.scheduleTime : null,
      subject: values.subject ?? null,
      body:
        values.channel === "0" // "0" is EMAIL
          ? values.emailFormat === "0"
            ? values.emailBody
            : values.emailFormat === "1"
              ? values.richTextBody
              : values.htmlTextBody
          : values.message,
      signature: values.signature
        ? parseInt(values.signature)
        : defaultSignatureId
          ? parseInt(defaultSignatureId)
          : null,
      footer: values.footer,
    };

    console.log("API Payload:", payload);

    sendBulkNotification(payload, {
      onSuccess: (data) => {
        console.log("Bulk message sent successfully:", data);
        form.reset();
      },
    });
  };

  const handleCancel = () => {
    form.reset();
  };

  return (
    <div>
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-6">
          <DynamicForm
            fields={formFields}
            form={form}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2 }}
            onSubmit={handleSubmit}
            onReset={handleCancel}
            loading={isPending}
            onFieldChange={handleFieldChange}
            config={{
              resetButtonText: "Cancel",
              submitButtonText: formData.scheduleForLater
                ? "Schedule Message"
                : "Send Bulk Message",
              layout: "grid",
              columns: 2,
              showSubmitButton: true,
              showResetButton: false,
              spacing: "medium",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700",
                buttonContainer: "flex justify-end gap-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            }}
          />
        </Card>
        <div className="col-span-6">
          <PreviewMessage
            channel={formData.channel}
            subject={formData.subject}
            message={formData.message}
            emailBody={formData.emailBody}
            richTextBody={formData.richTextBody}
            htmlTextBody={formData.htmlTextBody}
            footer={formData.footer}
            signature={formData.signature}
            emailFormat={formData.emailFormat}
            channelOptions={channelsOptions}
            signatureOptions={signatureOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateBulkMessage;
